/**
 * Detalle de un producto (ADM-02): todo lo que el backoffice sabe de el, en
 * una sola pantalla, con lo que se puede editar desde aca.
 *
 * No todo es editable. "Reparto con Random" (CLAUDE.md, seccion 9) es
 * terminante: la sincronizacion es dueña de nombre, marca, precio y stock, y
 * los pisa en cada corrida. Dejar que se editen aca seria un cambio que dura
 * hasta la proxima sincronizacion y nadie lo notaria hasta entonces. Esos
 * campos se muestran, nunca con un input al lado. Lo que si administra el
 * sitio —las fotos, la descripcion, la ficha tecnica, si esta publicado, si
 * esta destacado, el SEO y el paquete de venta (VTA-01)— se edita todo desde aca.
 * El paquete no es precio: dice de a cuantos se vende, y el precio del paquete
 * sale de multiplicar el de Random.
 *
 * "Volver" apunta a la lista con la pagina, la busqueda y los filtros que
 * tenia cuando se hizo clic (llega por `location.state`, lo arma
 * `Productos.tsx`): sin eso, volver perdia el lugar en una tabla de 322
 * productos.
 *
 * **Eliminar (ADM-02, 10-09-2026).** A diferencia de ocultar (reversible con
 * el mismo interruptor), esto borra la fila: pasa por una confirmacion en
 * modal que nombra el producto, mismo patron que dar de baja una cuenta en
 * `Administradores.tsx`. Es seguro —`ItemPedido` guarda una copia del
 * producto y no una referencia, asi que no rompe pedidos ya hechos— y esta
 * razonado en `eliminarProductoAdmin()` (backend).
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { FormEvent } from 'react';

import type { ActualizarSeoDto, ProductoAdminDetalle as Detalle } from '@elcarpintero/compartido';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { FormularioPaquete } from '../../componentes/administracion/FormularioPaquete';
import { FormularioSeo } from '../../componentes/administracion/FormularioSeo';
import { GaleriaProductoAdmin } from '../../componentes/administracion/GaleriaProductoAdmin';
import { Modal } from '../../componentes/administracion/Modal';
import { ModalAviso } from '../../componentes/administracion/ModalAviso';
import { PanelAdmin } from '../../componentes/administracion/PanelAdmin';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import {
  actualizarContenidoProducto,
  actualizarSeoProducto,
  actualizarVisibilidadProducto,
  eliminarProducto,
  obtenerProductoAdmin,
} from '../../servicios/administracion.servicio';
import { destacarProducto } from '../../servicios/portada.servicio';
import { pesos } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { esPaquete, textoDisponibles } from '../../utilidades/paquete';
import { rutas } from '../../utilidades/rutas';

export function ProductoDetalle() {
  const { id: idTexto } = useParams<{ id: string }>();
  const id = Number(idTexto);
  const ubicacion = useLocation();
  const navegar = useNavigate();
  const volver =
    (ubicacion.state as { volver?: string } | null)?.volver ?? rutas.administracion.productos();

  const peticion = usePeticion((senal) => obtenerProductoAdmin(id, senal), [id]);

  // La respuesta de cada guardado trae el producto completo y se pinta con
  // ella sin volver a pedir, igual que en Despacho.tsx.
  const [producto, setProducto] = useState<Detalle | null>(null);
  const [falla, setFalla] = useState<string | null>(null);
  // Por que no se pudo destacar (p. ej. la portada ya tiene doce); null lo cierra.
  const [fallaDestacar, setFallaDestacar] = useState<string | null>(null);

  useEffect(() => {
    if (peticion.datos !== null) setProducto(peticion.datos);
  }, [peticion.datos]);

  const [editandoSeo, setEditandoSeo] = useState(false);
  const [enviandoSeo, setEnviandoSeo] = useState(false);
  const [fallaSeo, setFallaSeo] = useState<string | null>(null);

  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [fallaEliminar, setFallaEliminar] = useState<string | null>(null);

  async function alternarVisible(visible: boolean): Promise<void> {
    if (producto === null) return;
    setFalla(null);
    try {
      await actualizarVisibilidadProducto(producto.id, visible);
      setProducto({ ...producto, visible });
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    }
  }

  async function alternarDestacado(destacado: boolean): Promise<void> {
    if (producto === null) return;
    setFalla(null);
    try {
      await destacarProducto(producto.id, destacado);
      setProducto({ ...producto, destacado });
    } catch (fallo) {
      setFallaDestacar(mensajeDe(fallo));
    }
  }

  async function guardarSeo(datos: ActualizarSeoDto): Promise<void> {
    if (producto === null) return;
    setFallaSeo(null);
    setEnviandoSeo(true);
    try {
      await actualizarSeoProducto(producto.id, datos);
      setProducto({ ...producto, ...datos });
      setEditandoSeo(false);
    } catch (fallo) {
      setFallaSeo(mensajeDe(fallo));
    } finally {
      setEnviandoSeo(false);
    }
  }

  async function eliminar(): Promise<void> {
    if (producto === null) return;
    setEliminando(true);
    setFallaEliminar(null);
    try {
      await eliminarProducto(producto.id);
      navegar(volver);
    } catch (fallo) {
      setFallaEliminar(mensajeDe(fallo));
      setEliminando(false);
    }
  }

  if (peticion.estado === 'cargando' && producto === null) return <Cargando que="el producto" />;
  if (peticion.estado === 'error' && producto === null) return <AvisoError error={peticion.error} />;
  if (producto === null) return null;

  return (
    <>
      <Seo
        titulo={producto.nombre}
        descripcion={`Detalle de ${producto.nombre} en el backoffice.`}
        ruta={rutas.administracion.productoDetalle(producto.id)}
        sinIndexar
      />

      <BarraAdmin
        titulo={producto.nombre}
        bajada={producto.sku}
        acciones={
          <Link className="btn btn-sm btn-contorno" to={volver}>
            <Icono nombre="flechaIzq" />
            Volver
          </Link>
        }
      />

      <div className="admin-cuerpo">
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            {falla}
          </p>
        ) : null}

        <GaleriaProductoAdmin
          productoId={producto.id}
          nombreProducto={producto.nombre}
          imagenes={producto.imagenes}
          onCambio={(imagenes) => {
            setProducto({ ...producto, imagenes });
          }}
        />

        <div className="panel-doble">
          <div>
            <PanelAdmin
              titulo="Datos del catálogo"
              bajada="Los manda Random; se actualizan solos en cada sincronización."
            >
              <ul className="lista-cifras">
                <li>
                  <span>Marca</span>
                  <span>{producto.marca ?? '—'}</span>
                </li>
                <li>
                  <span>Categoría</span>
                  <span>{producto.categoriaNombre}</span>
                </li>
                <li>
                  <span>{esPaquete(producto) ? 'Precio del paquete' : 'Precio'}</span>
                  <span className="num">{pesos(producto.precio)}</span>
                </li>
                <li>
                  <span>Stock</span>
                  {producto.estadoStock === 'agotado' ? (
                    <span className="etq etq-critico">Agotado</span>
                  ) : (
                    <span className="num">{textoDisponibles(producto, producto.stock)}</span>
                  )}
                </li>
              </ul>
            </PanelAdmin>

            <PanelAdmin
              titulo="Venta por paquete"
              bajada="Random no lo sabe: lo decide el sitio. Precio y stock se recalculan al guardar."
            >
              <FormularioPaquete
                producto={producto}
                onGuardado={(actualizado) => {
                  setProducto(actualizado);
                }}
              />
            </PanelAdmin>

            <PanelAdmin
              titulo="Descripción y ficha técnica"
              bajada="Contenido propio del sitio: esto Random no lo toca."
            >
              <FormularioContenido
                producto={producto}
                onGuardado={(datos) => {
                  setProducto({ ...producto, ...datos });
                }}
              />
            </PanelAdmin>
          </div>

          <div>
            <PanelAdmin titulo="Visibilidad">
              {/*
                El interruptor dice "Publicado", pero la tienda solo muestra lo
                que ademas tiene precio (`utilidades/publicacion.ts` en el
                backend). Sin este aviso, el producto parece roto.
              */}
              {producto.visible && producto.precio <= 0 ? (
                <p className="acceso-error" role="status">
                  <Icono nombre="alerta" />
                  <span>
                    Random lo informa sin precio, así que la tienda no lo muestra aunque esté
                    publicado. Vuelve a verse solo cuando Random le ponga precio.
                  </span>
                </p>
              ) : null}
              <div className="lista-interruptores">
                <button
                  type="button"
                  className="interruptor-boton"
                  aria-pressed={producto.visible}
                  onClick={() => {
                    void alternarVisible(!producto.visible);
                  }}
                >
                  <span className={producto.visible ? 'interruptor on' : 'interruptor'} />
                  <span>{producto.visible ? 'Publicado' : 'Oculto'}</span>
                </button>

                <button
                  type="button"
                  className="interruptor-boton"
                  aria-pressed={producto.destacado}
                  disabled={!producto.visible}
                  title={producto.visible ? undefined : 'Un producto oculto no aparece en la portada'}
                  onClick={() => {
                    void alternarDestacado(!producto.destacado);
                  }}
                >
                  <span className={producto.destacado ? 'interruptor on' : 'interruptor'} />
                  <span>{producto.destacado ? 'Destacado en portada' : 'No destacado'}</span>
                </button>
              </div>
            </PanelAdmin>

            <PanelAdmin
              titulo="SEO"
              extra={
                <button
                  type="button"
                  className="btn btn-sm btn-contorno"
                  onClick={() => {
                    setFallaSeo(null);
                    setEditandoSeo(true);
                  }}
                >
                  Editar
                </button>
              }
            >
              <ul className="lista-cifras">
                <li>
                  <span>Título</span>
                  <span>{producto.tituloSeo ?? 'Se genera solo'}</span>
                </li>
                <li>
                  <span>Descripción</span>
                  <span>{producto.descripcionSeo ?? 'Se genera sola'}</span>
                </li>
              </ul>
            </PanelAdmin>

            <PanelAdmin titulo="Eliminar producto">
              <p className="parrafo-panel">
                Borra el producto para siempre. Si solo quieres que deje de verse en la
                tienda, ocúltalo en vez de eliminarlo — eso se puede deshacer.
              </p>
              <button
                type="button"
                className="btn btn-sm btn-peligro"
                onClick={() => {
                  setFallaEliminar(null);
                  setConfirmandoEliminar(true);
                }}
              >
                Eliminar producto
              </button>
            </PanelAdmin>
          </div>
        </div>
      </div>

      <FormularioSeo
        entidad={editandoSeo ? producto : null}
        titulo={`SEO de ${producto.nombre}`}
        bajada="Título y descripción del resultado en buscadores"
        placeholderTitulo={producto.nombre}
        placeholderDescripcion="Se genera sola si se deja vacío"
        enviando={enviandoSeo}
        falla={fallaSeo}
        onCerrar={() => {
          setEditandoSeo(false);
        }}
        onGuardar={(datos) => {
          void guardarSeo(datos);
        }}
      />

      {/*
        Eliminar no tiene vuelta atras, asi que pasa por una confirmacion que
        nombra el producto. No se usa el `confirm()` del navegador: bloquea la
        pestaña entera y no se puede redactar (mismo patron que Administradores.tsx).
      */}
      <Modal
        titulo="Eliminar este producto"
        bajada="No se puede deshacer"
        abierto={confirmandoEliminar}
        onCerrar={() => {
          if (!eliminando) setConfirmandoEliminar(false);
        }}
      >
        {fallaEliminar !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{fallaEliminar}</span>
          </p>
        ) : null}

        <p className="parrafo-panel">
          <strong>{producto.nombre}</strong> ({producto.sku}) se borra para siempre, con sus
          fotos. Si solo quieres que deje de verse en la tienda, ocúltalo en vez de eliminarlo.
        </p>

        <div className="modal-acciones">
          <button
            type="button"
            className="btn btn-contorno"
            onClick={() => {
              setConfirmandoEliminar(false);
            }}
            disabled={eliminando}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-peligro"
            onClick={() => {
              void eliminar();
            }}
            disabled={eliminando}
          >
            {eliminando ? 'Eliminando…' : 'Eliminar el producto'}
          </button>
        </div>
      </Modal>

      <ModalAviso
        titulo="No se pudo destacar"
        mensaje={fallaDestacar}
        onCerrar={() => {
          setFallaDestacar(null);
        }}
      />
    </>
  );
}

interface DatosContenido {
  descripcion: string | null;
  fichaTecnica: string | null;
}

interface FormularioContenidoProps {
  producto: Detalle;
  onGuardado: (datos: DatosContenido) => void;
}

function FormularioContenido({ producto, onGuardado }: FormularioContenidoProps) {
  const [descripcion, setDescripcion] = useState(producto.descripcion ?? '');
  const [fichaTecnica, setFichaTecnica] = useState(producto.fichaTecnica ?? '');
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  async function guardar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setGuardando(true);
    setFalla(null);

    const datos: DatosContenido = {
      descripcion: descripcion.trim().length > 0 ? descripcion.trim() : null,
      fichaTecnica: fichaTecnica.trim().length > 0 ? fichaTecnica.trim() : null,
    };

    try {
      await actualizarContenidoProducto(producto.id, datos);
      onGuardado(datos);
      setGuardado(true);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form
      onSubmit={(evento) => {
        void guardar(evento);
      }}
    >
      {falla !== null ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          <span>{falla}</span>
        </p>
      ) : null}

      <div className="campo">
        <label htmlFor="prod-descripcion">Descripción</label>
        <textarea
          id="prod-descripcion"
          rows={5}
          value={descripcion}
          onChange={(evento) => {
            setDescripcion(evento.target.value);
            setGuardado(false);
          }}
        />
      </div>

      <div className="campo">
        <label htmlFor="prod-ficha">Ficha técnica</label>
        <textarea
          id="prod-ficha"
          rows={8}
          aria-describedby="prod-ficha-ayuda"
          value={fichaTecnica}
          onChange={(evento) => {
            setFichaTecnica(evento.target.value);
            setGuardado(false);
          }}
        />
        <p id="prod-ficha-ayuda" className="campo-ayuda">
          Una característica por línea, con dos puntos: «Largo: 450 mm». Va a la tabla de la ficha,
          después del código y la marca.
        </p>
      </div>

      <div className="admin-acciones">
        <button type="submit" className="btn btn-primario" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        {guardado ? (
          <span className="etiqueta-ok" role="status">
            <Icono nombre="check" />
            Guardado
          </span>
        ) : null}
      </div>
    </form>
  );
}
